// Site-wide motion: Lenis smooth scrolling + restrained GSAP reveals.
// - Smooth, weighted scrolling (Lenis) synced to GSAP's ticker.
// - Page headline eases in (blur-to-sharp, in the spirit of React Bits "BlurText").
// - Sections below the fold rise in once as they enter the viewport.
// Everything is skipped for visitors who ask their OS for reduced motion,
// and content is never hidden from crawlers (the HTML is fully visible; motion only runs after load).
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let lenis = null;

export default function MotionLayer() {
  const { pathname } = useLocation();

  // Smooth scrolling, created once.
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    lenis = new Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 0.9 });
    lenis.on('scroll', ScrollTrigger.update);
    const tick = (time) => lenis && lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      lenis = null;
    };
  }, []);

  // Per-page reveals.
  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    if (prefersReducedMotion()) return undefined;

    let ctx;
    const timer = window.setTimeout(() => {
      ctx = gsap.context(() => {
        const main = document.getElementById('root');
        if (!main) return;

        const h1 = main.querySelector('h1');
        if (h1) {
          gsap.fromTo(
            h1,
            { opacity: 0, y: 14, filter: 'blur(8px)' },
            { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.9, ease: 'power3.out', clearProps: 'filter,transform' },
          );
        }

        const viewportBottom = window.innerHeight;
        const sections = [...main.querySelectorAll('section')].filter((s) => {
          if (s.closest('header, footer, [role="dialog"], form')) return false;
          if (s.offsetHeight < 40 || !s.textContent.trim()) return false;
          return s.getBoundingClientRect().top > viewportBottom * 0.9; // only below the fold
        });
        if (!sections.length) return;
        gsap.set(sections, { opacity: 0, y: 28 });
        ScrollTrigger.batch(sections, {
          start: 'top 88%',
          once: true,
          onEnter: (batch) =>
            gsap.to(batch, { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out', stagger: 0.08, clearProps: 'transform' }),
        });
        // Safety net: never leave anything hidden (e.g. very tall pages, print, odd layouts).
        window.setTimeout(() => gsap.to(sections.filter((s) => getComputedStyle(s).opacity === '0' && s.getBoundingClientRect().top < window.innerHeight), { opacity: 1, y: 0, duration: 0.4 }), 2500);
      });
      ScrollTrigger.refresh();
    }, 250);

    return () => {
      window.clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, [pathname]);

  return null;
}
